import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { authAPI } from '../services/api';

const STORAGE_KEY = 'yuvanapi-manager-theme';
const LEGACY_KEY = 'yuvanapi-manager-theme-bg';
const DEFAULT = '#1E1E28';

export interface ManagerThemeColors {
  sidebarColor: string;
  headerColor: string;
  contentColor: string;
  sectionColor: string;
  tableColor: string;
}

interface ManagerThemeContextType extends ManagerThemeColors {
  setSidebarColor: (color: string) => void;
  setHeaderColor: (color: string) => void;
  setContentColor: (color: string) => void;
  setSectionColor: (color: string) => void;
  setTableColor: (color: string) => void;
  setTheme: (colors: Partial<ManagerThemeColors>) => void;
}

const ManagerThemeContext = createContext<ManagerThemeContextType | undefined>(undefined);

function loadTheme(): ManagerThemeColors {
  try {
    if (typeof window === 'undefined' || !window.localStorage)   return { sidebarColor: DEFAULT, headerColor: DEFAULT, contentColor: DEFAULT, sectionColor: '#353548', tableColor: '#334155' };
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as Partial<ManagerThemeColors>;
      return {
        sidebarColor: parsed.sidebarColor ?? DEFAULT,
        headerColor: parsed.headerColor ?? DEFAULT,
        contentColor: parsed.contentColor ?? DEFAULT,
        sectionColor: parsed.sectionColor ?? '#353548',
        tableColor: parsed.tableColor ?? '#334155',
      };
    }
    const legacy = window.localStorage.getItem(LEGACY_KEY);
    if (legacy) {
      return { sidebarColor: legacy, headerColor: legacy, contentColor: legacy, sectionColor: '#353548', tableColor: '#334155' };
    }
  } catch {
    // ignore
  }
  return { sidebarColor: DEFAULT, headerColor: DEFAULT, contentColor: DEFAULT, sectionColor: '#353548', tableColor: '#334155' };
}

export const ManagerThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setThemeState] = useState<ManagerThemeColors>(loadTheme);

  // Load theme from API for current manager (per-account, works across devices)
  useEffect(() => {
    const token = typeof window !== 'undefined' && window.localStorage && window.localStorage.getItem('token');
    if (!token) return;
    authAPI.getTheme()
      .then((res) => {
        const data = res?.data?.data;
        if (data && typeof data === 'object') {
          setThemeState({
            sidebarColor: data.sidebarColor ?? DEFAULT,
            headerColor: data.headerColor ?? DEFAULT,
            contentColor: data.contentColor ?? DEFAULT,
            sectionColor: data.sectionColor ?? '#353548',
            tableColor: data.tableColor ?? '#334155',
          });
        }
      })
      .catch(() => {
        // Keep localStorage/default on 401 or network error
      });
  }, []);

  useEffect(() => {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(theme));
      }
    } catch {
      // ignore
    }
  }, [theme]);

  const setTheme = useCallback((colors: Partial<ManagerThemeColors>) => {
    setThemeState((prev) => ({ ...prev, ...colors }));
  }, []);

  const setSidebarColor = useCallback((color: string) => setThemeState((p) => ({ ...p, sidebarColor: color })), []);
  const setHeaderColor = useCallback((color: string) => setThemeState((p) => ({ ...p, headerColor: color })), []);
  const setContentColor = useCallback((color: string) => setThemeState((p) => ({ ...p, contentColor: color })), []);
  const setSectionColor = useCallback((color: string) => setThemeState((p) => ({ ...p, sectionColor: color })), []);
  const setTableColor = useCallback((color: string) => setThemeState((p) => ({ ...p, tableColor: color })), []);

  return (
    <ManagerThemeContext.Provider
      value={{
        ...theme,
        setSidebarColor,
        setHeaderColor,
        setContentColor,
        setSectionColor,
        setTableColor,
        setTheme,
      }}
    >
      {children}
    </ManagerThemeContext.Provider>
  );
};

export function useManagerTheme(): ManagerThemeContextType {
  const ctx = useContext(ManagerThemeContext);
  if (ctx === undefined) {
    return {
      sidebarColor: DEFAULT,
      headerColor: DEFAULT,
      contentColor: DEFAULT,
      sectionColor: '#353548',
      tableColor: '#334155',
      setSidebarColor: () => {},
      setHeaderColor: () => {},
      setContentColor: () => {},
      setSectionColor: () => {},
      setTableColor: () => {},
      setTheme: () => {},
    };
  }
  return ctx;
}
